import { startCase as _startCase } from 'lodash'
import { RRule, RRuleSet } from 'rrule'
import moment from 'moment-timezone'

export function levelArrayToString(skillLevelsArray, options = {}) {
  const { maxLength } = options
  if (skillLevelsArray.length === 0) return ''

  return skillLevelsArray
    .sort()
    .map((s) => _startCase(s))
    .reduce((result, skillLevel) => {
      const [_, levelName, levelNumber] = skillLevel.match(/([^0-9]+)(\d*)/)
      let output
      if (result.includes(levelName)) {
        output = [result, levelNumber].join(', ')
      } else if (!result.includes(levelName)) {
        output = [result, skillLevel].join(' – ')
      }
      return output.length > maxLength ? `${skillLevelsArray.length} Levels` : output
    })
}

export function alphabetizeByFullName(a, b) {
  if (a.fullName < b.fullName) return -1
  if (a.fullName > b.fullName) return 1
  return 0
}

export function selectedToTop(a, b) {
  if ((a.selected && b.selected) || (!a.selected && !b.selected)) return 0
  if (a.selected && !b.selected) return -1
  if (!a.selected && b.selected) return 1
}

export function requiredToTop(a, b) {
  if ((a.required && b.required) || (!a.required && !b.required)) return 0
  if (a.required && !b.required) return -1
  if (!a.required && b.required) return 1
}

export function priceInDollars(priceInCents) {
  return (priceInCents / 100.0).toFixed(2)
}

export function RRuleFromIceCubeClass(iceCubeClass) {
  switch (iceCubeClass) {
    case 'IceCube::SecondlyRule':
      return RRule.SECONDLY

    case 'IceCube::MinutelyRule':
      return RRule.MINUTELY

    case 'IceCube::HourlyRule':
      return RRule.HOURLY

    case 'IceCube::DailyRule':
      return RRule.DAILY

    case 'IceCube::WeeklyRule':
      return RRule.WEEKLY

    case 'IceCube::MonthlyRule':
      return RRule.MONTHLY

    case 'IceCube::YearlyRule':
      return RRule.YEARLY

    default:
      throw Error('Unknown IceCube class type')
  }
}

// Can provide a ruleSet as optional second arg to combine ruleSets
export function ruleSetFromRecurrenceRule(recurrenceRule, ruleSet = new RRuleSet()) {
  recurrenceRule.rrules.forEach((rrule) => {
    // Create npm rrule rules based on rrules from server
    ruleSet.rrule(
      new RRule({
        freq: RRuleFromIceCubeClass(rrule.ruleType),
        interval: rrule.interval,
        count: rrule.count,
        dtstart: moment(recurrenceRule.startTime.time).toDate()
      })
    )
  })

  // Create npm rrule extimes based on rrules from server
  recurrenceRule.extimes.forEach((extime) => {
    ruleSet.exdate(moment(extime.time || extime).toDate())
  })

  // Create npm rrule rtimes/rdates based on rrules from server
  recurrenceRule.rtimes.forEach((rtime) => {
    ruleSet.rdate(moment(rtime.time || rtime).toDate())
  })

  return ruleSet
}

const DAY_ABBR_TO_VALUE = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
const WEEKS_TO_GENERATE_FOR_COPY = 52

function getFirstUpcomingDay(dayValue) {
  const today = moment()
  const todayDay = today.day()

  if (todayDay === dayValue) {
    return today.clone().startOf('day')
  }

  const daysAhead = (dayValue - todayDay + 7) % 7
  return today.clone().add(daysAhead, 'days').startOf('day')
}

function getDayValuesFromKlass(klass) {
  if (klass.daysOfWeek?.length) {
    return klass.daysOfWeek.map((abbr) => DAY_ABBR_TO_VALUE[abbr]).filter((v) => v !== undefined)
  }

  if (klass.dayOfWeek) {
    return klass.dayOfWeek.split('/').map((abbr) => DAY_ABBR_TO_VALUE[abbr.trim()]).filter((v) => v !== undefined)
  }

  return []
}

function getScheduleSlotsFromRecurrenceRule(recurrenceRule, timeZone) {
  if (!recurrenceRule?.rtimes?.length) return []

  const slotsByDay = new Map()

  recurrenceRule.rtimes.forEach((rtime) => {
    const occurrence = moment(rtime.time || rtime).tz(timeZone)
    const dayValue = occurrence.day()

    if (!slotsByDay.has(dayValue)) {
      slotsByDay.set(dayValue, {
        dayValue,
        hour: occurrence.hour(),
        minute: occurrence.minute()
      })
    }
  })

  return [...slotsByDay.values()].sort((a, b) => a.dayValue - b.dayValue)
}

function generateRecurringDatesFromSlots(slots, timeZone) {
  const dates = []

  slots.forEach(({ dayValue, hour, minute }) => {
    const firstDay = getFirstUpcomingDay(dayValue)
    for (let i = 0; i < WEEKS_TO_GENERATE_FOR_COPY; i++) {
      dates.push({
        date: firstDay.clone().add(i, 'w').tz(timeZone).set({ hour, minute }),
        isTimeSet: true
      })
    }
  })

  return dates.sort((a, b) => a.date.valueOf() - b.date.valueOf())
}

function generateRecurringDates(dayValues, timeOfDay, timeZone) {
  const parsed = moment(timeOfDay, 'h:mm A')
  const slots = dayValues.map((dayValue) => ({
    dayValue,
    hour: parsed.hour(),
    minute: parsed.minute()
  }))

  return generateRecurringDatesFromSlots(slots, timeZone)
}

export function datesFromKlassForCopy(klass, timeZone) {
  if (klass.dates?.length) {
    return klass.dates.map((date) => ({
      date: moment(date).tz(timeZone),
      isTimeSet: true
    }))
  }

  const recurrenceSlots = getScheduleSlotsFromRecurrenceRule(klass.recurrenceRule, timeZone)
  if (recurrenceSlots.length > 0) {
    return generateRecurringDatesFromSlots(recurrenceSlots, timeZone)
  }

  if (!klass.timeOfDay) return []

  const dayValues = getDayValuesFromKlass(klass)
  if (dayValues.length === 0) return []

  return generateRecurringDates(dayValues, klass.timeOfDay, timeZone)
}

export function readableSkateType(skateType) {
  const readableSkateTypes = {
    men_figure: { label: "Men's Figure", color: 'blue' },
    women_figure: { label: "Women's Figure", color: 'red' },
    youth_figure: { label: 'Youth Figure', color: 'purple' },
    men_hockey: { label: "Men's Hockey", color: 'gray' },
    women_hockey: { label: "Women's Hockey", color: 'green' },
    youth_hockey: { label: 'Youth Hockey', color: 'orange' }
  }

  return readableSkateTypes[skateType]
}
